import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { OrderNotesSidebarService } from "../../../../core/services/order-notes-sidebar.service";
import { v4 as uuidv4 } from "uuid";
import { DialogModule } from "primeng/dialog";
import { CommonModule } from "@angular/common";
import { SalesOrderNotes } from "../../../../core/models/orders/salesOrderNotes";
import { LoaderService } from "../../../../core/services/loader.service";
import { DrawerModule } from "primeng/drawer";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { InputSwitchModule } from "primeng/inputswitch";
import { ButtonModule } from "primeng/button";
import { ToggleSwitchModule } from "primeng/toggleswitch";
@UntilDestroy()
@Component({
  selector: "jni-order-notes-sidebar",
  templateUrl: "./order-notes-sidebar.component.html",
  styleUrls: ["./order-notes-sidebar.component.scss"],
  imports: [
    DialogModule,
    SelectModule,
    CommonModule,
    FormsModule,
    DrawerModule,
    ReactiveFormsModule,
    InputTextModule,
    InputSwitchModule,
    ButtonModule,
    ToggleSwitchModule,
  ],
})
export class OrderNotesSidebarComponent implements OnChanges {
  @Input() display!: boolean;
  @Input() saleOrderId = "";
  @Input() isFromInvoiceSummary = false;
  @Input() displaySideBeforeSubmittingStatus = false;
  @Output() displayChange = new EventEmitter<boolean>();
  @Output() refreshOrderNotesGrid = new EventEmitter();
  @Output() closeSidebar = new EventEmitter();
  @Output() closeSidebarAndCallStatusApprovalCall = new EventEmitter();
  @Input() proposalIdD365EditFieldValidation = false;
  @Input() orderType = "";
  saleOrderNoteForm!: FormGroup;
  defaultTypeSelection = "";
  defaultPurchaseOrderSelection = "";
  typeList = [
    {
      id: 1,
      name: "Billing note",
    },
    {
      id: 2,
      name: "Collections",
    },
    // 11286 - PGL-270 Remove Invoice Note option From All Note Forms
    // {
    //   id: 3,
    //   name: 'Invoice note'
    // },
    {
      id: 4,
      name: "Order note",
    },
  ];
  numberFieldStatus = true;
  purchaseOrderNumber: string[] = [];
  ispurchaseOrderIdField = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _toastr: JniToastrService,
    private _orderNotesSidebarService: OrderNotesSidebarService
  ) {
    this.saleOrderNoteForm = this._formBuilder.group({
      id: 0,
      salesOrderId: [""],
      type: this.isFromInvoiceSummary ? [""] : ["", Validators.required],
      purchaseOrderStatus: [false],
      purchaseOrderId: [""],
      description: ["", Validators.required],
      note: [""],
    });
  }

  onHide() {
    this.displayChange.emit(false);
    this.closeSidebarModel();
  }

  ngOnChanges() {
    if (this.proposalIdD365EditFieldValidation) {
      this.typeList = [
        {
          id: 1,
          name: "Billing note",
        },
      ];
    }
  }

  openPurchaseOrder(event: Event) {
    if (event.target instanceof HTMLInputElement && event.target.checked) {
      this.getAllLinePoNumber(this.saleOrderId);
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
    } else {
      this.numberFieldStatus = true;
      this.removeCustomerPoRequiredField();
      this.saleOrderNoteForm.get("purchaseOrderId")?.setValue("");
    }
  }

  getAllLinePoNumber(saleOrderId: string) {
    if (saleOrderId) {
      this._orderNotesSidebarService
        .getAllLinePoNumber(saleOrderId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this.purchaseOrderNumber = res.data;
          },
          error: () => {
            this._loaderService.setLoaderState(false);
          },
        });
    }
  }

  setCustomerPoRequiredField() {
    if (!this.isFromInvoiceSummary) {
      this.ispurchaseOrderIdField = true;
      this.saleOrderNoteForm
        .get("purchaseOrderId")
        ?.setValidators(Validators.required);
      this.saleOrderNoteForm.get("purchaseOrderId")?.updateValueAndValidity();
    }
  }

  removeCustomerPoRequiredField() {
    this.ispurchaseOrderIdField = false;
    this.saleOrderNoteForm.get("purchaseOrderId")?.clearValidators();
    this.saleOrderNoteForm.get("purchaseOrderId")?.updateValueAndValidity();
  }

  onSaleOrderNotesSave() {
    this._loaderService.setLoaderState(true);

    const saleOrderFormObject =
      this.saleOrderNoteForm.getRawValue() as SalesOrderNotes;
    if (this.saleOrderId) {
      saleOrderFormObject.salesOrderId = this.saleOrderId;
      saleOrderFormObject.portalKey = uuidv4();
    }

    this._orderNotesSidebarService
      .AddSalesOrderNotes(saleOrderFormObject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          this.clearOrderNoteForm();
          if (res.data) {
            if (this.displaySideBeforeSubmittingStatus) {
              this.closeSidebarAndCallStatusApprovalCall.emit();
            }
            this.closeSidebar.emit();
            this.refreshOrderNotesGrid.emit();
            this._toastr.success(
              "Success",
              res.message || "Order note added successfully"
            );
          }
        },
        error: res => {
          //this.clearOrderNoteForm();
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Error adding order note");
        },
      });
  }

  closeSidebarModel() {
    this.closeSidebar.emit();
  }

  patchValueInOrderNote(salesOrderNotes: SalesOrderNotes) {
    this.saleOrderNoteForm?.patchValue({
      id: salesOrderNotes.id,
      type: salesOrderNotes.type,
      purchaseOrderStatus: salesOrderNotes?.purchaseOrderId ? true : false,
      purchaseOrderId: salesOrderNotes.purchaseOrderId,
      description: salesOrderNotes.description,
      note: salesOrderNotes.note,
    });

    if (salesOrderNotes?.purchaseOrderId) {
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
    } else {
      this.numberFieldStatus = true;
      this.defaultPurchaseOrderSelection = "";
      this.removeCustomerPoRequiredField();
    }
  }

  clearOrderNoteForm() {
    this.numberFieldStatus = this.isFromInvoiceSummary ? false : true;
    this.defaultPurchaseOrderSelection = "";
    this.removeCustomerPoRequiredField();
    this.saleOrderNoteForm?.patchValue({
      id: 0,
      type: this.isFromInvoiceSummary ? "Billing note" : "",
      purchaseOrderStatus: false,
      purchaseOrderId: "",
      description: "",
      note: "",
    });

    if (this.isFromInvoiceSummary) {
      this.setCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: true,
      });
    }
  }

  onPurchaseOrderSelection(name: string) {
    if (name === "Billing note") {
      this.getAllLinePoNumber(this.saleOrderId);
      this.numberFieldStatus = false;
      this.setCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: true,
      });
    }
    //  else if (name === "Order note") {
    //   // this.numberFieldStatus = false;
    //   // this.setCustomerPoRequiredField();
    //   // this.saleOrderNoteForm.patchValue({
    //   //   purchaseOrderStatus: true
    //   // });
    // }
    else {
      this.numberFieldStatus = true;
      this.defaultPurchaseOrderSelection = "";
      this.removeCustomerPoRequiredField();
      this.saleOrderNoteForm.patchValue({
        purchaseOrderStatus: false,
      });
    }
  }
}
